import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Button, Container, Loader, Stack, Text, Title } from '@mantine/core'
import { ArrowLeft } from 'lucide-react'
import { learningMaterialsCatalogApi, type LearningMaterialItem } from '../../api/learningMaterials'
import { RichText } from '../../components/common/RichText'

function extractYoutubeVideoId(url: string | null | undefined): string | null {
  if (!url?.trim()) return null
  const u = url.trim()
  const short = u.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/)
  if (short?.[1]) return short[1]
  const watch = u.match(/[?&]v=([a-zA-Z0-9_-]{11})/)
  if (watch?.[1]) return watch[1]
  const embed = u.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/)
  if (embed?.[1]) return embed[1]
  return null
}

function isYoutubeUrl(url: string | null | undefined): boolean {
  if (!url) return false
  return /youtube\.com|youtu\.be/i.test(url)
}

function isLikelyPdf(url: string | null | undefined, mediaType: string | null | undefined): boolean {
  const t = (mediaType || '').toLowerCase()
  if (t === 'pdf') return true
  if (t === 'document') return /\.pdf(\?|$)/i.test(url || '')
  return /\.pdf(\?|$)/i.test(url || '')
}

export function MaterialDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [item, setItem] = useState<LearningMaterialItem | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const numericId = id ? Number(id) : NaN

  useEffect(() => {
    if (!id || Number.isNaN(numericId)) {
      setError('Invalid material id')
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    learningMaterialsCatalogApi
      .get(numericId)
      .then((res) => setItem(res.data))
      .catch(() => setError('Could not load this material.'))
      .finally(() => setLoading(false))
  }, [id, numericId])

  const mediaType = (item?.media_type || '').toLowerCase()
  const mediaUrl = item?.media_url || ''

  const youtubeId = useMemo(() => {
    if (mediaType !== 'video' || !mediaUrl) return null
    if (!isYoutubeUrl(mediaUrl)) return null
    return extractYoutubeVideoId(mediaUrl)
  }, [mediaType, mediaUrl])

  const showPdfButton = isLikelyPdf(mediaUrl, item?.media_type ?? null)

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Box py="xl" style={{ display: 'flex', justifyContent: 'center' }}>
          <Loader />
        </Box>
      </Container>
    )
  }

  if (error || !item) {
    return (
      <Container size="md" py="xl">
        <Button
          variant="subtle"
          leftSection={<ArrowLeft size={18} />}
          onClick={() => navigate(-1)}
          mb="md"
        >
          Back
        </Button>
        <Text c="red">{error || 'Not found'}</Text>
      </Container>
    )
  }

  return (
    <Container size="md" py="xl">
      <Button
        variant="subtle"
        leftSection={<ArrowLeft size={18} />}
        onClick={() => navigate(-1)}
        mb="lg"
      >
        Back
      </Button>

      <Title order={2} mb="xs" c="var(--sat-text)">
        {item.title}
      </Title>
      <Text size="sm" c="dimmed" mb="lg">
        {item.media_type || '—'}
        {mediaUrl ? ` · ${mediaUrl}` : ''}
      </Text>

      <Stack gap="xl">
        {item.content && (
          <Box className="rich-text-wrap">
            <RichText size="md">{item.content}</RichText>
          </Box>
        )}

        {mediaType === 'audio' && mediaUrl && (
          <Box>
            <audio src={mediaUrl} controls style={{ width: '100%', maxWidth: 560 }} />
          </Box>
        )}

        {mediaType === 'video' && mediaUrl && (
          <Box>
            {youtubeId ? (
              <Box
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 560,
                  aspectRatio: '16 / 9',
                }}
              >
                <iframe
                  title="YouTube"
                  width="560"
                  height="315"
                  style={{ border: 0, width: '100%', height: '100%', position: 'absolute', inset: 0 }}
                  src={`https://www.youtube.com/embed/${youtubeId}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            ) : (
              <video src={mediaUrl} controls style={{ width: '100%', maxWidth: 560 }} />
            )}
          </Box>
        )}

        {showPdfButton && mediaUrl && (
          <Box>
            <Button component="a" href={mediaUrl} target="_blank" rel="noopener noreferrer">
              Read
            </Button>
          </Box>
        )}
      </Stack>
    </Container>
  )
}
